import { useAppToken } from "../../../theme/v6/useAppToken";
import React from "react";
import { Button, Dropdown, Typography } from "antd";
import type { MenuProps } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DownOutlined, GlobalOutlined, LogoutOutlined, UserOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { AuthActions } from "../../../auth/actions";
import { getLanguageMenuLabel, languageLabels, supportedLanguages } from "../LanguageSelector";
import { makeSelectLanguage } from "../../../containers/App/selectors";
import { useWorkspaceCompactHeader } from "../../../utils/useMediaQuery";
import WorkCompanySwitcher from "../WorkCompanySwitcher";

type Props = { email: string; showProfile?: boolean };

const UserMenu = React.memo(({ email, showProfile = true }: Props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation("translations");
  const language = useSelector(makeSelectLanguage());
  const compactHeader = useWorkspaceCompactHeader();
  const { token } = useAppToken();

  const handleLogout = React.useCallback(() => {
    navigate("/", { replace: true });
    dispatch(AuthActions.logout());
  }, [dispatch, navigate]);

  const accountMenuItems = React.useMemo<MenuProps["items"]>(
    () => [
      ...(showProfile
        ? [
            {
              key: "profile",
              icon: <UserOutlined />,
              label: <Link to="/personal-area/profile">{t("profile")}</Link>,
            },
          ]
        : []),
      {
        key: "language",
        icon: <GlobalOutlined />,
        label: `${t("language")}: ${languageLabels[language]}`,
        children: supportedLanguages.map((supportedLanguage) => ({
          key: `language-${supportedLanguage}`,
          label: getLanguageMenuLabel(supportedLanguage),
          onClick: () => void i18n.changeLanguage(supportedLanguage),
        })),
      },
      { type: "divider" },
      {
        danger: true,
        key: "logout",
        icon: <LogoutOutlined />,
        label: t("logout"),
        onClick: handleLogout,
      },
    ],
    [handleLogout, i18n, language, showProfile, t],
  );

  return (
    <>
      <WorkCompanySwitcher />
      <Dropdown menu={{ items: accountMenuItems, selectedKeys: [`language-${language}`] }} placement="bottomRight" trigger={["click"]}>
        <Button
          aria-label={`${t("profile")}: ${email}`}
          icon={<UserOutlined style={{ color: token.colorPrimary }} />}
          style={{ maxWidth: 280, minWidth: 0 }}
          type="text"
        >
          {!compactHeader && (
            <Typography.Text ellipsis={{ tooltip: email }} style={{ maxWidth: 200 }}>
              {email}
            </Typography.Text>
          )}
          <DownOutlined aria-hidden />
        </Button>
      </Dropdown>
    </>
  );
});

export default UserMenu;
